import {
  AccessUserType,
  Catalogue_SystemSettingsViewType,
  getAcademicNamespace,
  getCoreAcademicNamespace,
} from '@tyro/api';
import { getGradeSets } from '@tyro/assessments';
import { AttendanceCodes, getAttendanceCodes } from '@tyro/attendance';
import { getCalendarDayBellTimes, getCalendarEventTags } from '@tyro/calendar';
import { getNumber, lazyWithRetry, throw404Error } from '@tyro/core';
import { GearIcon } from '@tyro/icons';
import {
  getBehaviourCategories,
  getContactsForSelect,
  getNoteTags,
  getNoteTagsBehaviour,
  getStaffForSelect,
  getStudentsForSelect,
} from '@tyro/people';
import { getEmploymentCapacities } from '@tyro/people/src/api/staff/employment-capacities';
import { getStaffPosts } from '@tyro/people/src/api/staff/staff-posts';
import { type NavObjectFunction, NavObjectType } from '@tyro/router/types';
import { redirect } from '@tyro/router/utils';
import { AbsenceTypes, getStaffWorkAbsenceTypes } from '@tyro/substitution';
import dayjs from 'dayjs';
import { getCommentBanks } from './api/comment-banks/comment-banks';
import { getCustomFields } from './api/custom-fields/custom-fields';
import { getFormB } from './api/dtr-returns/form-b';
import { getPermissionGroups } from './api/permissions/user-permissions-groups';
import { getPermissionSets } from './api/permissions/user-permissions-sets';
import { getPpodCredentialsStatus } from './api/ppod/ppod-credentials-status';
import { getSchoolsInfo } from './api/ppod/school-details';
import { getSyncRequests } from './api/ppod/sync-requests';
import { getCoreRoomPools } from './api/rooms/room-pools';
import { getCoreRooms } from './api/rooms/rooms';
import { getCalendarDayInfo } from './api/school-calendar/calendar-day-info';
import { getCatalogueSubjects } from './api/subjects';
import { getSettingsView } from './api/system/view';
import { getUserThirdParties } from './api/user-access/third-parties/list';
import { getUserAccess } from './api/user-access/user-access';
import { customFieldsPaths } from './components/custom-fields/container';

const Rooms = lazyWithRetry(() => import('./pages/rooms'));
const AcademicYearsList = lazyWithRetry(() => import('./pages/academic-years'));
const Subjects = lazyWithRetry(() => import('./pages/subjects'));
const Ppod = lazyWithRetry(() => import('./pages/ppod/ppod'));
const Login = lazyWithRetry(() => import('./pages/ppod/login'));
const Sync = lazyWithRetry(() => import('./pages/ppod/sync'));
const SchoolDetails = lazyWithRetry(
  () => import('./pages/ppod/school-details'),
);
const DTRReturns = lazyWithRetry(
  () => import('./pages/dtr-returns/dtr-returns'),
);
const DTRReturnsFileB = lazyWithRetry(
  () => import('./pages/dtr-returns/file-b'),
);
const Exports = lazyWithRetry(() => import('./pages/exports'));
const Import = lazyWithRetry(() => import('./pages/import'));
const UserAccessContainer = lazyWithRetry(
  () => import('./components/user-access/user-access-container'),
);
const UserAccessStaffPage = lazyWithRetry(
  () => import('./pages/user-access/staff'),
);
const UserAccessStudentsPage = lazyWithRetry(
  () => import('./pages/user-access/students'),
);
const UserAccessContactsPage = lazyWithRetry(
  () => import('./pages/user-access/contacts'),
);
const UserAccessThirdPartiesPage = lazyWithRetry(
  () => import('./pages/user-access/third-parties'),
);
const Permissions = lazyWithRetry(() => import('./pages/permissions'));
const CreatePermission = lazyWithRetry(
  () => import('./pages/permissions/create'),
);
const EditPermission = lazyWithRetry(() => import('./pages/permissions/edit'));
const ClonePermission = lazyWithRetry(
  () => import('./pages/permissions/clone'),
);
const NoteLabel = lazyWithRetry(() => import('./pages/note-label'));
const CommentBanks = lazyWithRetry(
  () => import('./pages/comment-banks/comment-banks'),
);
const CalendarTags = lazyWithRetry(() => import('./pages/calendar-tags'));
const Comments = lazyWithRetry(() => import('./pages/comment-banks/comments'));
const SchoolCalendarOverview = lazyWithRetry(
  () => import('./pages/school-calendar'),
);
const RoomsContainer = lazyWithRetry(
  () => import('./components/rooms/rooms-container'),
);
const RoomPools = lazyWithRetry(() => import('./pages/room-pools'));
const BehaviourContainer = lazyWithRetry(
  () => import('./components/behaviour/behaviour-container'),
);
const BehaviourCategories = lazyWithRetry(
  () => import('./pages/behaviour-settings/categories'),
);
const BehaviourType = lazyWithRetry(
  () => import('./pages/behaviour-settings/types'),
);

const CustomFieldsContainer = lazyWithRetry(
  () => import('./components/custom-fields/container'),
);
const CustomFields = lazyWithRetry(() => import('./pages/custom-fields'));
const GradeSets = lazyWithRetry(() => import('./pages/grade-sets'));
const CreateGradeSet = lazyWithRetry(() => import('./pages/grade-sets/create'));
const EditGradeSet = lazyWithRetry(() => import('./pages/grade-sets/edit'));

const SystemSettingsPage = lazyWithRetry(() => import('./pages/system'));
const PersonalSettingsPage = lazyWithRetry(
  () => import('./pages/system/personal'),
);

export const getRoutes: NavObjectFunction = (t) => [
  {
    type: NavObjectType.Category,
    title: t('navigation:management.title'),
    children: [
      {
        type: NavObjectType.RootGroup,
        title: t('navigation:management.settings.title'),
        path: '/settings',
        icon: <GearIcon />,
        hasAccess: ({ permissions }) => permissions.isStaffUser,
        children: [
          {
            type: NavObjectType.MenuLink,
            path: 'attendance-codes',
            title: t('navigation:general.attendance.codes'),
            loader: () => getAttendanceCodes({}),
            hasAccess: ({ permissions }) =>
              permissions.hasPermission(
                'ps:1:attendance:view_attendance_codes',
              ),
            element: <AttendanceCodes />,
          },
          {
            title: t('navigation:management.settings.schoolCalendar'),
            type: NavObjectType.MenuLink,
            path: 'school-calendar',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:general_admin:view_school_day_calendar_screen',
              ),
            loader: async () => {
              const { activeAcademicNamespace } = await getAcademicNamespace();
              const dayInfoPromise = getCalendarDayInfo({});
              const startDate = dayjs(activeAcademicNamespace?.startDate);
              const endDate = dayjs(activeAcademicNamespace?.endDate);

              return Promise.all([
                getCalendarDayBellTimes({
                  fromDate: startDate.format('YYYY-MM-DD'),
                  toDate: endDate.format('YYYY-MM-DD'),
                }),
                dayInfoPromise,
              ]);
            },
            element: <SchoolCalendarOverview />,
          },
          {
            type: NavObjectType.MenuLink,
            path: 'absence-types',
            title: t('navigation:general.absence.types'),
            loader: () => getStaffWorkAbsenceTypes({}),
            hasAccess: ({ permissions }) =>
              permissions.hasPermission(
                'ps:1:staff_work_management:absences_codes_write',
              ),
            element: <AbsenceTypes />,
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.subjects'),
            path: 'subjects',
            loader: () => getCatalogueSubjects(),
            hasAccess: ({ permissions }) =>
              permissions.hasPermission('ps:1:general_admin:read_subjects'),
            element: <Subjects />,
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.rooms'),
            path: 'rooms',
            element: <RoomsContainer />,
            hasAccess: ({ permissions }) =>
              permissions.hasPermission('ps:1:general_admin:read_rooms'),
            children: [
              {
                type: NavObjectType.NonMenuLink,
                index: true,
                loader: () => redirect('./list'),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'list',
                element: <Rooms />,
                loader: () =>
                  getCoreRooms({
                    disabled: false,
                  }),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'pools',
                element: <RoomPools />,
                loader: () => getCoreRoomPools({ ids: [] }),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'inactive-rooms',
                element: <Rooms />,
                loader: () =>
                  getCoreRooms({
                    disabled: true,
                  }),
              },
            ],
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.academicYears'),
            path: 'academic-years',
            loader: () => getCoreAcademicNamespace(),
            hasAccess: ({ permissions }) =>
              permissions.hasPermission(
                'ps:1:general_admin:read_academic_namespaces',
              ),
            element: <AcademicYearsList />,
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.userAccess'),
            path: 'user-access',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:users:user_access_management',
              ),
            loader: () => {
              const userType = AccessUserType.Staff;
              return getUserAccess({ userType });
            },
            element: <UserAccessContainer />,
            children: [
              {
                type: NavObjectType.NonMenuLink,
                index: true,
                loader: () => redirect('./staff'),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'staff',
                loader: () => getUserAccess({ userType: AccessUserType.Staff }),
                element: <UserAccessStaffPage />,
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'contacts',
                loader: () =>
                  getUserAccess({ userType: AccessUserType.Contact }),
                element: <UserAccessContactsPage />,
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'students',
                loader: () =>
                  getUserAccess({ userType: AccessUserType.Student }),
                element: <UserAccessStudentsPage />,
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'third-parties',
                loader: () => getUserThirdParties(),
                hasAccess: ({ permissions }) =>
                  permissions.isStaffUserWithPermission(
                    'ps:1:users:third_party_provision',
                  ),
                element: <UserAccessThirdPartiesPage />,
              },
            ],
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.permissions'),
            path: 'permissions',
            element: <Permissions />,
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:users:permission_management',
              ),
            loader: () =>
              Promise.all([
                getPermissionGroups({ custom: true }),
                getPermissionGroups({ custom: false }),
                getStudentsForSelect({}),
                getContactsForSelect({}),
                getStaffForSelect({}),
                getPermissionSets({}),
                getPermissionSets({ student: true }),
                getPermissionSets({ contact: true }),
                getPermissionSets({ staff: true }),
              ]),
          },
          {
            type: NavObjectType.NonMenuLink,
            path: 'permissions/create',
            element: <CreatePermission />,
          },
          {
            type: NavObjectType.NonMenuLink,
            path: 'permissions/edit/:permissionGroupId',
            loader: ({ params }) => {
              const permissionGroupId = getNumber(params?.permissionGroupId);
              if (!permissionGroupId) {
                throw404Error();
              }

              return getPermissionGroups({ ids: [permissionGroupId] });
            },
            element: <EditPermission />,
          },
          {
            type: NavObjectType.NonMenuLink,
            path: 'permissions/clone/:permissionGroupId',
            loader: ({ params }) => {
              const permissionGroupId = getNumber(params?.permissionGroupId);
              if (!permissionGroupId) {
                throw404Error();
              }

              return getPermissionGroups({ ids: [permissionGroupId] });
            },
            element: <ClonePermission />,
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.ppod'),
            path: 'ppod',
            hasAccess: ({ permissions, featureFlags }) =>
              featureFlags.featureFlag_settings_peapod &&
              permissions.hasPermission('ps:1:general_admin:ppod_sync'),
            loader: () => getPpodCredentialsStatus(),
            element: <Ppod />,
            children: [
              {
                type: NavObjectType.NonMenuLink,
                index: true,
                loader: () => redirect('./sync'),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'sync',
                element: <Sync />,
                loader: () => getSyncRequests({}),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'details',
                element: <SchoolDetails />,
                loader: () => getSchoolsInfo(),
              },
            ],
          },
          {
            type: NavObjectType.NonMenuLink,
            path: 'ppod/login',
            hasAccess: ({ permissions }) =>
              permissions.hasPermission('ps:1:general_admin:ppod_set_password'),
            element: <Login />,
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.imports'),
            path: 'import',
            hasAccess: ({ permissions }) =>
              permissions.isTyroUser ||
              permissions.isStaffUserHasWithAtLeastOnePermission([
                'api:assessment:write:results',
                'api:core:write:student_create',
                'ps:1:documents:write_student_photos',
              ]),
            element: <Import />,
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.exports'),
            path: 'exports',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:printing_and_exporting:export_ptm',
              ),
            element: <Exports />,
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.dtrReturns'),
            path: 'dtr-returns',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:general_admin:permissions_dtr',
              ),
            element: <DTRReturns />,
          },
          {
            type: NavObjectType.NonMenuLink,
            path: 'dtr-returns/file-b',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:general_admin:permissions_dtr',
              ),
            loader: () =>
              Promise.all([
                getFormB({}),
                getStaffPosts(),
                getEmploymentCapacities(),
              ]),
            element: <DTRReturnsFileB />,
          },
          {
            title: t('navigation:management.settings.noteLabels'),
            type: NavObjectType.MenuLink,
            path: 'note-labels',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:general_admin:notes_settings',
              ),
            loader: () => getNoteTags(),
            element: <NoteLabel />,
          },
          {
            title: t('navigation:management.settings.calendarTags'),
            type: NavObjectType.MenuLink,
            path: 'calendar-tags',
            element: <CalendarTags />,
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:calendar:administer_calendar',
              ),
            loader: () => getCalendarEventTags({}),
          },
          {
            title: t('navigation:management.settings.customFields'),
            type: NavObjectType.MenuLink,
            path: 'custom-fields',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:general_admin:view_custom_fields',
              ),
            element: <CustomFieldsContainer />,
            children: [
              {
                type: NavObjectType.NonMenuLink,
                index: true,
                loader: () => redirect('./students'),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: ':source',
                element: <CustomFields />,
                loader: ({ params }) =>
                  getCustomFields({
                    type: customFieldsPaths[params?.source || 'students'],
                  }),
              },
            ],
          },
          {
            title: t('navigation:management.settings.behaviourLabels'),
            type: NavObjectType.MenuLink,
            path: 'behaviour',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:general_admin:behaviour_settings',
              ),
            element: <BehaviourContainer />,
            children: [
              {
                type: NavObjectType.NonMenuLink,
                index: true,
                loader: () => redirect('./types'),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'types',
                element: <BehaviourType />,
                loader: () => getNoteTagsBehaviour(),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'categories',
                element: <BehaviourCategories />,
                loader: () => getBehaviourCategories({}),
              },
            ],
          },
          {
            title: t('navigation:management.settings.gradeSets'),
            type: NavObjectType.MenuLink,
            path: 'grade-sets',
            hasAccess: ({ permissions }) =>
              permissions.hasPermission('ps:1:assessment:write_grade_sets'),
            children: [
              {
                type: NavObjectType.NonMenuLink,
                index: true,
                element: <GradeSets />,
                loader: () => getGradeSets({ ids: [] }),
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'create',
                element: <CreateGradeSet />,
              },
              {
                type: NavObjectType.NonMenuLink,
                path: 'edit/:id',
                element: <EditGradeSet />,
                loader: ({ params }) => {
                  const id = getNumber(params?.id);

                  if (!id) {
                    throw404Error();
                  }

                  return getGradeSets({ ids: [id] });
                },
              },
            ],
          },
          {
            title: t('navigation:management.settings.commentBanks'),
            type: NavObjectType.MenuLink,
            path: 'comment-banks',
            hasAccess: ({ permissions }) =>
              permissions.isStaffUserWithPermission(
                'ps:1:assessment:write_comment_banks',
              ),
            loader: () => getCommentBanks({}),
            element: <CommentBanks />,
          },
          {
            type: NavObjectType.NonMenuLink,
            path: 'comment-banks/:id',
            hasAccess: ({ permissions }) => permissions.isStaffUser,
            loader: ({ params }) => {
              const id = getNumber(params?.id);

              if (!id) {
                throw404Error();
              }

              return getCommentBanks({ ids: [id] });
            },
            element: <Comments />,
          },
          {
            type: NavObjectType.MenuLink,
            title: t('navigation:management.settings.system'),
            path: 'system',
            hasAccess: ({ permissions }) =>
              permissions.hasAtLeastOnePermission([
                'ps:1:general_admin:write_personal_settings',
                'ps:1:general_admin:write_school_settings',
              ]),
            element: <SystemSettingsPage />,
            loader: () =>
              Promise.all([
                getSettingsView({
                  type: Catalogue_SystemSettingsViewType.Personal,
                  module: [],
                }),
                getSettingsView({
                  type: Catalogue_SystemSettingsViewType.Tenant,
                  module: [],
                }),
              ]),
          },
          {
            type: NavObjectType.NonMenuLink,
            title: t('navigation:management.settings.personalSettings'),
            path: 'personal',
            hasAccess: ({ permissions }) =>
              permissions.hasPermission(
                'ps:1:general_admin:write_personal_settings',
              ),
            element: <PersonalSettingsPage />,
          },
        ],
      },
    ],
  },
];
